<template>
  <div>
    <v-col cols="12" class="px-0 pb-0">
      <v-btn color="primary" icon @click="handleBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <span class="text-h6 mr-2" style="vertical-align: middle">
        {{ $t('message.pages.videoSpeed.videoSpeed') }}
      </span>
      <helper-tooltip :click="handleKnowMore" />
    </v-col>
    <v-col cols="12" class="pb-2 pt-6">
      <span>
        {{ $t('message.pages.videoSpeed.videoSpeedMessage') }}
      </span>
    </v-col>
    <v-container :class="{ 'd-none': !loadingSlider }">
      <v-row class="d-flex">
        <v-col cols="12">
          <v-skeleton-loader
            class="border loader-width pr-5 skeleton-slider"
            type="image"
          ></v-skeleton-loader>
        </v-col>
        <v-col cols="12">
          <div class="skeleton-btn-loader-container">
            <v-skeleton-loader
              class="border pr-5 skeleton-button"
              type="button"
            ></v-skeleton-loader>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-slider
      v-model="videoSpeed"
      :max="5"
      :ticks="true"
      :tick-labels="tickLabels"
      class="px-2 py-4"
      :class="{ 'd-none': loadingSlider }"
      show-ticks="always"
      step="1"
      tick-size="5"
    ></v-slider>
    <v-row no-gutters v-if="hasEditVideoPerm">
      <v-col cols="12">
        <v-card-actions class="px-3 pt-4 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            :class="{ 'd-none': loadingSlider }"
            color="primary"
            :disabled="loadingButtonSave || disableBtn"
            :loading="loadingButtonSave"
            @click="handleUpdateVideoSpeed"
          >
            {{ $t('message.pages.video.publish') }}
          </v-btn>
        </v-card-actions>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import profileApi from '@/services/profileApi'
import linkHelper from '@/utils/linkHelper.mixin.js'
import HelperTooltip from '@/components/HelperTooltip.vue'

export default {
  name: 'VideoPlayerSpeed',
  mixins: [linkHelper],
  components: {
    HelperTooltip,
  },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    tickLabels: ['Normal', '1.1x', '1.2x', '1.3x', '1.4x', '1.5x'],
    loadingButtonSave: false,
    withoutChanges: true,
    videoId: null,
    videoSpeed: null,
    videoSpeedList: [1, 1.1, 1.2, 1.3, 1.4, 1.5],
    defaultSpeed: null,
    disableBtn: false,
    loadingSlider: true,
  }),
  watch: {
    videoSpeed: {
      handler() {
        this.setButtonDisabling()
      },
    },
  },
  mounted() {
    this.videoId = this.liveVideoId ? this.liveVideoId : this.$route.params.id
    this.getDefaultSpeed()
  },
  computed: {
    ...mapState({
      profile: (state) => state.video.profile,
      video: (state) => state.video.video,
      profile_id: (state) => state.video.profile_id,
    }),
    hasEditVideoPerm() {
      return (
        this.$store.state.user.userPermissions?.includes('put-video') ?? false
      )
    },
  },
  methods: {
    ...mapActions('video', [
      'updateProfile',
      'createProfile',
      'setProfileToVideos',
      'getVideoConfig',
    ]),
    async handleUpdateVideoSpeed() {
      this.loadingButtonSave = true
      const settedSpeed = this.videoSpeedList.at(this.videoSpeed)
      await this.setProfile()

      try {
        await this.updateProfile([
          this.profileId,
          {
            general: {
              default_speed: settedSpeed,
            },
          },
        ])
        this.loadingButtonSave = false
        this.defaultSpeed = this.videoSpeed
        this.setButtonDisabling()
      } catch (e) {
        console.error(e)
        this.loadingButtonSave = false
      }
    },
    async setProfile() {
      if (!this.video.config.profile) {
        await this.createProfile(this.videoId)
        this.video.config.profile = this.profile_id
        this.profileId = this.profile_id
        await this.setProfileToVideos([this.profileId, this.videoId])
      } else {
        this.profileId = this.video.config.profile
      }
    },
    handleSkeletonLoader() {
      this.loadingSlider = false
    },
    async getDefaultSpeed() {
      await this.setProfile()
      const profileId = this.video.config.profile
      const {
        data: {
          general: { default_speed },
        },
      } = await profileApi.getProfileId(profileId)

      const index = this.videoSpeedList.findIndex((object) => {
        return object === default_speed
      })
      this.videoSpeed = index
      this.defaultSpeed = index
      this.setButtonDisabling()
      this.handleSkeletonLoader()
    },
    setButtonDisabling() {
      this.disableBtn = this.defaultSpeed === this.videoSpeed ? true : false
    },
    handleKnowMore() {
      const link = this.getVideoSpeedLink()
      window.open(link, '_blank')
    },
  },
}
</script>
<style scoped lang="scss">
::v-deep .v-input__slider {
  .v-input__control {
    .v-input__slot {
      .v-slider__ticks-container {
        .v-slider__tick {
          opacity: 1 !important;
          height: 10px !important;
          width: 2px !important;
          top: -4px !important;
          .v-slider__tick-label {
            top: 16px !important;
          }
          &:first-child {
            .v-slider__tick-label {
              color: var(--v-primary-base) !important;
              font-weight: bold !important;
            }
          }
        }
      }
      .v-slider__track-container {
        height: 10px;
        .v-slider__track-background {
          background-color: #e3e3e8 !important;
        }
      }
      .v-slider__thumb-container {
        .v-slider__thumb {
          height: 20px !important;
          width: 20px !important;
          &::before {
            top: -8px !important;
            left: -8px !important;
          }
        }
      }
    }
  }
}
.loader-width {
  width: 100%;
}
.skeleton-btn-loader-container {
  display: flex;
  justify-content: flex-end;
}
::v-deep .skeleton-button {
  .v-skeleton-loader__button {
    width: 130px;
    height: 36px;
  }
}
::v-deep .skeleton-slider {
  .v-skeleton-loader__image {
    height: 100px;
  }
}
</style>
